import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, MailOpen, FileText, Loader2 } from "lucide-react";

const STATUS = {
  pending: { key: "snapshot_requests_status_pending", cls: "bg-amber-100 text-amber-700" },
  approved: { key: "snapshot_requests_status_approved", cls: "bg-emerald-100 text-emerald-700" },
  approved_no_data: { key: "snapshot_requests_status_approved_no_data", cls: "bg-secondary text-muted-foreground" },
  declined: { key: "snapshot_requests_status_declined", cls: "bg-rose-100 text-rose-700" },
};

export default function SnapshotRequests() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("snapshot_requests_inbox"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/snapshot-requests/inbox"); setRows(data?.requests || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setActingId(r.id);
    try {
      const { data } = await api.post(`/snapshot-requests/${r.id}/approve`);
      await load();
      if (data?.report_id) toast.success(t("snapshot_approved_toast"), { description: t("open_report"), action: { label: t("open_report"), onClick: () => navigate(`/app/weva/report/${data.report_id}`) } });
      else toast.success(t("snapshot_approved_toast"));
    } catch (e) {
      const msg = String(apiError(e, ""));
      if (msg.toLowerCase().includes("no data")) toast.error(t("snapshot_no_data_msg"));
      else toast.error(t("popup_approve_failed"), { description: msg });
    } finally { setActingId(null); }
  };

  const decline = async (r) => {
    setActingId(r.id);
    try { await api.post(`/snapshot-requests/${r.id}/decline`); await load(); toast.success(t("snapshot_declined_toast")); }
    catch (e) { toast.error(apiError(e, t("error"))); } finally { setActingId(null); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="snapshot-inbox-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="sri-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-2xl text-foreground">{t("snapshot_requests_inbox")}</h1>
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="sri-empty">
          <MailOpen className="h-8 w-8 text-muted-foreground/60" />
          <p className="max-w-sm text-sm text-muted-foreground">{t("snapshot_requests_none")}</p>
        </div>
      ) : rows.map((r) => {
        const pending = r.status === "pending";
        const acting = actingId === r.id;
        const st = STATUS[r.status] || STATUS.pending;
        return (
          <div key={r.id} data-testid={`sri-item-${r.id}`} className="space-y-2 rounded-2xl border border-border bg-secondary/60 p-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <p className="min-w-0 flex-1 truncate font-display text-sm text-foreground">{t("snapshot_requests_from", { coach: r.coach_name || "" })}</p>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${st.cls}`}>{t(st.key)}</span>
            </div>
            <p className="text-xs font-medium text-muted-foreground">{t("snapshot_requests_period", { days: r.period_days })}</p>
            {r.note ? <p className="rounded-lg border-l-4 border-primary bg-background p-2.5 text-sm text-foreground">{r.note}</p> : null}
            <p className="text-xs text-muted-foreground/70">{fmt(r.created_at)}</p>

            {pending ? (
              <div className="flex gap-2 pt-1">
                <button data-testid={`sri-decline-${r.id}`} onClick={() => decline(r)} disabled={acting} className="flex-1 rounded-full border border-border py-2.5 text-sm font-medium text-muted-foreground disabled:opacity-50">{t("snapshot_requests_decline")}</button>
                <button data-testid={`sri-approve-${r.id}`} onClick={() => approve(r)} disabled={acting} className="flex flex-1 items-center justify-center rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                  {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("snapshot_requests_approve")}
                </button>
              </div>
            ) : null}

            {r.status === "approved" && r.report_id ? (
              <button data-testid={`sri-open-report-${r.id}`} onClick={() => navigate(`/app/weva/report/${r.report_id}`)} className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-2 text-sm font-medium text-primary">
                <FileText className="h-3.5 w-3.5" /> {t("open_report")}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
